import React from "react";
import { SOLUTIONS, INDUSTRIES, PACKAGES, FAQS, TESTIMONIALS, CASE_STUDIES } from "@/data/content";
import { CALENDLY_URL, FOUNDER_IMG } from "@/data/constants";
import { api } from "@/lib/api";
import { Link, useLocation } from "react-router-dom";
import TrustBar from "@/components/TrustBar";
import Honeypot from "@/components/Honeypot";
import VideoShowcase from "@/components/VideoShowcase";
import PhoneMockup from "@/components/PhoneMockup";
import { HomeEnhancementsTop, HomeEnhancementsClose } from "@/pages/HomeEnhancements";
import { useAttribution, nowISO } from "@/hooks/useAttribution";
import { useFormTimer } from "@/hooks/useFormTimer";
import { calendlyUrl, rememberIndustry, track } from "@/lib/analytics";

const SERVICE_OPTIONS = [
  "AI Receptionist",
  "AI Business Automation",
  "AI Audit",
  "AI Academy",
  "General Inquiry",
];

function splitName(full) {
  const t = (full || "").trim();
  if (!t) return { first_name: null, last_name: null };
  const i = t.indexOf(" ");
  if (i < 0) return { first_name: t, last_name: null };
  return { first_name: t.slice(0, i), last_name: t.slice(i + 1) };
}

const HERO_IMG = "https://images.pexels.com/photos/31650443/pexels-photo-31650443.jpeg";

// -------- HERO --------
function Hero() {
  return (
    <section className="relative min-h-[92vh] flex items-center pt-24 pb-20 overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-60"/>
        <div className="absolute inset-0 hero-overlay"/>
      </div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-8">
          <div className="overline fade-up">Luxury AI Consulting · Denver, CO</div>
          <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1.05] mt-6 fade-up d1" data-testid="hero-headline">
            AI systems designed to help <br className="hidden md:block"/>
            <em className="text-brand">serious businesses</em> work smarter.
          </h1>
          <p className="text-secondary text-lg md:text-xl font-light leading-relaxed mt-8 max-w-2xl fade-up d2">
            We build intelligent AI voice receptionists, automation systems, and bespoke AI consulting for entrepreneurs who refuse to miss another lead. Calm, capable, on-brand — twenty-four hours a day.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 fade-up d3">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="hero-book-cta">Book a Consultation</a>
            <a href="#audit" className="btn-ghost" data-testid="hero-audit-cta">Try the AI Business Audit →</a>
          </div>
        </div>
      </div>
    </section>
  );
}

// -------- SOLUTIONS --------
function Solutions() {
  return (
    <section id="solutions" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="overline">02 · Solutions</div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mt-4">
          <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight leading-tight md:col-span-7">
            Organized by the <em className="text-brand">problem</em> you're solving — not the tech.
          </h2>
          <p className="text-secondary md:col-span-5 leading-relaxed">A modern operations layer for your business. Each system is built on top of your existing stack and trained on your voice.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-16">
          {SOLUTIONS.map((s, i) => (
            <article key={s.slug} className={`editorial-card p-8 md:p-10 ${s.span} md:col-span-${s.span.includes('8') ? '8' : '4'}`} data-testid={`solution-${s.slug}`}>
              <div className="overline">{String(i+1).padStart(2,'0')}</div>
              <div className="font-display text-3xl md:text-4xl tracking-tight mt-4">{s.problem}</div>
              <div className="text-brand text-sm tracking-[0.15em] uppercase mt-3">{s.product}</div>
              <p className="text-secondary mt-5 leading-relaxed">{s.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- INDUSTRIES --------
function Industries() {
  return (
    <section id="industries" className="py-24 md:py-32 bg-surface">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="overline">03 · Industries</div>
        <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight mt-4 max-w-3xl">Built for the way your industry actually runs.</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-16">
          {INDUSTRIES.map((ind) => (
            <Link to={`/#pricing?industry=${ind.slug}`} key={ind.slug}
              onClick={() => { rememberIndustry(ind.slug); track("industry_card_click", { industry: ind.slug }); }} className="group relative aspect-[4/5] overflow-hidden editorial-card" data-testid={`industry-${ind.slug}`}>
              <img src={ind.img} alt={ind.name} className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-60 transition-opacity duration-700 grayscale group-hover:grayscale-0"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"/>
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <div className="font-display text-2xl md:text-3xl text-primary">{ind.name}</div>
                <div className="text-xs uppercase tracking-widest text-brand mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">See Example →</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- ROI CALCULATOR --------
function ROICalculator() {
  const [calls, setCalls] = React.useState(400);
  const [missed, setMissed] = React.useState(25);
  const [acv, setAcv] = React.useState(2500);
  const [close, setClose] = React.useState(20);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/roi/calculate", {
          monthly_calls: Number(calls), missed_calls_pct: Number(missed),
          avg_customer_value: Number(acv), close_rate_pct: Number(close),
        });
        setResult(data);
      } catch (err) {
        // Network error — keep last known result.
        void err;
      }
    }, 200);
    return () => clearTimeout(t);
  }, [calls, missed, acv, close]);

  const fmt = (n) => `$${Number(n||0).toLocaleString(undefined, {maximumFractionDigits: 0})}`;

  return (
    <section id="roi" className="py-24 md:py-32 bg-surface">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="overline">04 · ROI Calculator</div>
          <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight mt-4">How much revenue is your phone losing every month?</h2>
          <p className="text-secondary mt-6 leading-relaxed">Adjust the sliders. The numbers update instantly. We assume an AI receptionist recovers ~80% of missed opportunities.</p>
        </div>
        <div className="lg:col-span-7 space-y-8">
          {[
            { label: "Monthly Calls", value: calls, set: setCalls, min: 50, max: 2000, step: 10, unit: "" },
            { label: "Missed Calls %", value: missed, set: setMissed, min: 0, max: 60, step: 1, unit: "%" },
            { label: "Avg Customer Value", value: acv, set: setAcv, min: 100, max: 25000, step: 100, unit: "$", isMoney: true },
            { label: "Close Rate %", value: close, set: setClose, min: 5, max: 80, step: 1, unit: "%" },
          ].map((s) => (
            <div key={s.label}>
              <div className="flex justify-between items-baseline">
                <label className="overline">{s.label}</label>
                <span className="font-display text-2xl text-brand">{s.isMoney ? fmt(s.value) : `${s.value}${s.unit}`}</span>
              </div>
              <input type="range" min={s.min} max={s.max} step={s.step} value={s.value} onChange={(e)=>s.set(e.target.value)} className="lux mt-3" data-testid={`roi-${s.label.toLowerCase().replace(/[\s%]/g,'-')}-slider`}/>
            </div>
          ))}
          {result && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              <div className="editorial-card p-6"><div className="overline">Monthly Revenue Lost</div><div className="font-display text-3xl text-[#9E7A7A] mt-2" data-testid="roi-monthly-lost">{fmt(result.monthly_revenue_lost)}</div></div>
              <div className="editorial-card p-6"><div className="overline">Annual Revenue Lost</div><div className="font-display text-3xl text-[#9E7A7A] mt-2" data-testid="roi-annual-lost">{fmt(result.annual_revenue_lost)}</div></div>
              <div className="editorial-card p-6 tracing-beam"><div className="overline">Potential Annual Recovery</div><div className="font-display text-3xl text-brand mt-2" data-testid="roi-recovery">{fmt(result.estimated_recovery_annual)}</div></div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// -------- AI BUSINESS AUDIT --------
const AUDIT_INITIAL_FORM = {
  first_name: "", last_name: "", business_name: "", email: "", phone: "", website: "",
  industry: "Real Estate",
  monthly_calls: 300, missed_calls_pct: 20, avg_customer_value: 2500,
  has_receptionist: false, uses_ai: false, biggest_pain: "",
};
const AUDIT_INDUSTRIES = [
  "Real Estate", "Insurance", "Law", "Medical", "Dental", "Med Spa",
  "HVAC", "Plumbing", "Roofing", "Contractors", "Property Mgmt", "Restaurants", "Other",
];

function AuditForm({ form, setForm, hp, setHp, loading, err, onSubmit }) {
  const patch = (k) => (e) => setForm({
    ...form,
    [k]: e.target.type === "checkbox" ? e.target.checked
      : (typeof form[k] === "number" ? Number(e.target.value) : e.target.value),
  });
  return (
    <form onSubmit={onSubmit} className="mt-12 bg-surface p-8 md:p-12 border border-white/5 grid grid-cols-1 md:grid-cols-2 gap-6">
      <Honeypot value={hp} onChange={setHp}/>
      <div><label className="overline block mb-2">First Name *</label><input required className="input-line" value={form.first_name} onChange={patch("first_name")} data-testid="audit-first-name"/></div>
      <div><label className="overline block mb-2">Last Name</label><input className="input-line" value={form.last_name} onChange={patch("last_name")} data-testid="audit-last-name"/></div>
      <div><label className="overline block mb-2">Business *</label><input required className="input-line" value={form.business_name} onChange={patch("business_name")} data-testid="audit-business"/></div>
      <div><label className="overline block mb-2">Email *</label><input required type="email" className="input-line" value={form.email} onChange={patch("email")} data-testid="audit-email"/></div>
      <div><label className="overline block mb-2">Phone</label><input type="tel" className="input-line" value={form.phone} onChange={patch("phone")} data-testid="audit-phone"/></div>
      <div><label className="overline block mb-2">Website</label><input className="input-line" value={form.website} onChange={patch("website")} data-testid="audit-website"/></div>
      <div><label className="overline block mb-2">Industry</label>
        <select className="input-line" value={form.industry} onChange={patch("industry")} data-testid="audit-industry">
          {AUDIT_INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>
      <div><label className="overline block mb-2">Monthly Calls</label><input type="number" className="input-line" value={form.monthly_calls} onChange={patch("monthly_calls")} data-testid="audit-calls"/></div>
      <div><label className="overline block mb-2">Missed Calls %</label><input type="number" className="input-line" value={form.missed_calls_pct} onChange={patch("missed_calls_pct")} data-testid="audit-missed"/></div>
      <div><label className="overline block mb-2">Avg Customer Value $</label><input type="number" className="input-line" value={form.avg_customer_value} onChange={patch("avg_customer_value")} data-testid="audit-acv"/></div>
      <div className="flex items-center gap-3"><input type="checkbox" id="has_r" checked={form.has_receptionist} onChange={patch("has_receptionist")} data-testid="audit-has-recept"/><label htmlFor="has_r" className="text-secondary text-sm">I have a receptionist</label></div>
      <div className="flex items-center gap-3"><input type="checkbox" id="uses_ai" checked={form.uses_ai} onChange={patch("uses_ai")} data-testid="audit-uses-ai"/><label htmlFor="uses_ai" className="text-secondary text-sm">I already use AI tools</label></div>
      <div className="md:col-span-2"><label className="overline block mb-2">What's your biggest operational pain?</label>
        <textarea rows="3" className="input-box" value={form.biggest_pain} onChange={patch("biggest_pain")} placeholder="e.g. We're losing leads after 5pm…" data-testid="audit-pain"/>
      </div>
      <button type="submit" disabled={loading} className="btn-primary md:col-span-2 w-fit" data-testid="audit-submit">
        {loading ? "Running audit…" : "Run My Audit →"}
      </button>
      {err && <p className="text-[#9E7A7A] text-sm md:col-span-2">{err}</p>}
    </form>
  );
}

function AuditResultTier({ report }) {
  if (!report.lead_tier) return null;
  return (
    <div className="inline-flex items-center gap-3 px-4 py-2 border border-brand-hi mb-6">
      <span className="overline">Lead Tier</span>
      <span className={`font-display text-2xl ${report.lead_tier === 'HOT' ? 'text-brand' : 'text-primary'}`} data-testid="audit-tier">{report.lead_tier}</span>
      <span className="text-secondary text-xs uppercase tracking-widest">Score {report.lead_score}/100</span>
    </div>
  );
}

function AuditResultScores({ scores }) {
  const items = [
    ["Revenue Score", scores.revenue_score],
    ["AI Readiness Score", scores.ai_readiness_score],
    ["Missed Opportunity Score", scores.missed_opportunity_score],
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {items.map(([label, val]) => (
        <div key={label} className="editorial-card p-8"><div className="overline">{label}</div><div className="font-display text-6xl text-brand mt-3">{val}<span className="text-2xl text-muted">/100</span></div></div>
      ))}
    </div>
  );
}

function AuditResultMissedRevenue({ estimate }) {
  return (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="editorial-card p-8"><div className="overline">Monthly Missed Revenue</div><div className="font-display text-4xl text-[#9E7A7A] mt-3">${estimate.monthly.toLocaleString()}</div></div>
      <div className="editorial-card p-8"><div className="overline">Annual Missed Revenue</div><div className="font-display text-4xl text-[#9E7A7A] mt-3">${estimate.annual.toLocaleString()}</div></div>
    </div>
  );
}

function AuditResultNarrative({ aiReport }) {
  if (!aiReport?.executive_summary) return null;
  return (
    <div className="mt-10 editorial-card p-10 tracing-beam">
      <div className="overline">Executive Summary</div>
      <p className="text-primary text-lg leading-relaxed mt-4 italic">{aiReport.executive_summary}</p>
      {aiReport.top_recommendations && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
          {aiReport.top_recommendations.map((r, i) => (
            <div key={r.title || i}><div className="overline">{String(i+1).padStart(2,'0')}</div><div className="font-display text-2xl mt-2">{r.title}</div><p className="text-secondary text-sm mt-2">{r.why}</p><p className="text-brand text-xs mt-2 uppercase tracking-widest">{r.expected_impact}</p></div>
          ))}
        </div>
      )}
    </div>
  );
}

function AuditResult({ report }) {
  return (
    <div className="mt-12 fade-up" data-testid="audit-result">
      <AuditResultTier report={report} />
      <AuditResultScores scores={report.scores} />
      <AuditResultMissedRevenue estimate={report.missed_revenue_estimate} />
      <AuditResultNarrative aiReport={report.ai_report} />
    </div>
  );
}

function BusinessAudit() {
  const attribution = useAttribution();
  const { elapsedMs } = useFormTimer();
  const [hp, setHp] = React.useState("");
  const [form, setForm] = React.useState(AUDIT_INITIAL_FORM);
  const [loading, setLoading] = React.useState(false);
  const [report, setReport] = React.useState(null);
  const [err, setErr] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.post("/ai/business-audit", {
        ...form,
        attribution,
        submitted_at: nowISO(),
      });
      setReport(data.report);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not run audit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="audit" className="py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="overline">Free Tool</div>
        <h2 className="font-display font-light text-4xl md:text-6xl tracking-tighter leading-[1.05] mt-6">
          The <em className="text-brand">AI Business Audit</em>
        </h2>
        <p className="text-secondary text-lg mt-6 max-w-2xl">Answer eight questions. We'll return three scores, your missed-revenue estimate, and a personalized strategy.</p>

        <AuditForm form={form} setForm={setForm} hp={hp} setHp={setHp} loading={loading} err={err} onSubmit={submit} />

        {report && <AuditResult report={report} />}
      </div>
    </section>
  );
}

// -------- PRICING --------
const MASONRY_ROW = 4;
const MASONRY_GAP = 32;

// Grid-row span masonry: DOM/tab order stays in service order; reflows on any size change.
function MasonryItem({ children }) {
  const ref = React.useRef(null);
  const [span, setSpan] = React.useState(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    const update = () => setSpan(Math.ceil((el.getBoundingClientRect().height + MASONRY_GAP) / MASONRY_ROW));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div style={span ? { gridRowEnd: `span ${span}` } : undefined}>
      <div ref={ref}>{children}</div>
    </div>
  );
}

function CaseStudyBody({ cs }) {
  return (
    <>
      <div className="font-display text-xl mt-5 leading-snug">{cs.business}</div>
      <div className="mt-5 space-y-5 text-secondary text-sm leading-relaxed">
        <div>
          <div className="overline text-[10px] text-primary/70 mb-1">The Challenge</div>
          <p>{cs.challenge}</p>
        </div>
        <div>
          <div className="overline text-[10px] text-primary/70 mb-1">Our Approach</div>
          <p>{cs.solution}</p>
        </div>
        <div>
          <div className="overline text-[10px] text-primary/70 mb-1">
            Delivery · <span className="text-secondary">{cs.deliverySummary}</span>
          </div>
          <ul className="mt-2 space-y-2">
            {cs.delivery.map((d) => (
              <li key={d} className="flex gap-3"><span className="text-brand">—</span>{d}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="overline text-[10px] text-primary/70 mb-1">Operational Outcome</div>
          <p>{cs.outcome}</p>
        </div>
      </div>
      <p className="mt-5 border-l-2 border-brand/50 pl-4 text-[11px] text-secondary italic leading-relaxed opacity-80">
        {cs.disclaimer}
      </p>
    </>
  );
}

function CaseStudyTabs({ items, pkgId, focusId, focusKey }) {
  const [active, setActive] = React.useState(0);
  const tabs = React.useRef([]);
  const viewExample = (i) => {
    rememberIndustry(items[i].id);
    track("lead_gen_example_view", { industry: items[i].id });
  };
  React.useEffect(() => {
    const i = items.findIndex((c) => c.id === focusId);
    if (i < 0) return;
    setActive(i);
    viewExample(i);
  }, [items, focusId, focusKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const select = (i) => {
    if (i === active) return;
    setActive(i);
    viewExample(i);
  };
  const onKeyDown = (e) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const next = (active + (e.key === "ArrowRight" ? 1 : -1) + items.length) % items.length;
    select(next);
    tabs.current[next]?.focus();
  };
  const cs = items[active];
  return (
    <div id={`case-${pkgId}`} className="mt-6 pt-6 border-t border-white/5 scroll-mt-28" data-testid={`pricing-case-${pkgId}`}>
      <div className="flex items-center gap-3">
        <div className="h-px w-6 bg-brand" aria-hidden="true" />
        <div className="text-brand text-xs uppercase tracking-[0.22em]">{cs.label}</div>
      </div>
      <div role="tablist" aria-label="Illustrative examples by industry" onKeyDown={onKeyDown}
        className="mt-5 flex flex-wrap gap-x-6 gap-y-3 border-b border-white/10">
        {items.map((c, i) => (
          <button key={c.id} ref={(el) => { tabs.current[i] = el; }} type="button" role="tab"
            id={`case-tab-${pkgId}-${c.id}`} aria-selected={i === active} aria-controls={`case-panel-${pkgId}`}
            tabIndex={i === active ? 0 : -1} onClick={() => select(i)}
            className={`case-tab ${i === active ? "is-active" : ""}`} data-testid={`case-tab-${c.id}`}>
            {c.industry}
          </button>
        ))}
      </div>
      <div key={cs.id} role="tabpanel" id={`case-panel-${pkgId}`} aria-labelledby={`case-tab-${pkgId}-${cs.id}`}
        className="case-fade" data-testid={`case-panel-${cs.id}`}>
        <CaseStudyBody cs={cs} />
      </div>
    </div>
  );
}

// All packages route to the Calendly AI Strategy Call. Payment is collected
// later via Stripe invoice after the live demo + proposal + contract steps.
function Pricing() {
  const { hash, key } = useLocation();
  const industry = hash.startsWith("#pricing?") ? new URLSearchParams(hash.split("?")[1]).get("industry") : null;
  // Deep link (#pricing?industry=hvac): open Lead Gen details, select tab, scroll to the example.
  React.useEffect(() => {
    if (!industry) return;
    const details = document.getElementById("receive-lead_generation");
    if (details) details.open = true;
    // Retries let the scroll settle while images/videos above are still shifting layout on first load.
    const scrollToCase = () => {
      const el = document.getElementById("case-lead_generation");
      if (el && Math.abs(el.getBoundingClientRect().top - 112) > 40) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const timers = [120, 900, 1800].map((ms) => setTimeout(scrollToCase, ms));
    return () => timers.forEach(clearTimeout);
  }, [industry, key]);
  const bookStrategyCall = () => {
    window.open(calendlyUrl(), "_blank", "noopener,noreferrer");
  };
  return (
    <section id="pricing" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="overline">05 · Investment</div>
        <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight mt-4 max-w-3xl">Transparent starting points. Tailored to your business.</h2>
        <p className="text-secondary mt-4 max-w-2xl">Final pricing varies with business size, integrations, and custom workflows. All packages include onboarding and 30 days of optimization.</p>
        <div className="pricing-masonry mt-16" data-testid="pricing-grid">
          {PACKAGES.map((p) => (
            <MasonryItem key={p.id}>
            <div className={`p-8 md:p-10 editorial-card ${p.featured ? "tracing-beam" : ""}`} data-testid={`pricing-${p.id}`}>
              {p.featured && <div className="overline text-brand mb-4">Most Popular</div>}
              <div className="font-display text-3xl">{p.name}</div>
              <div className="text-secondary text-sm uppercase tracking-widest mt-2">{p.subtitle}</div>
              {p.isFree ? (
                <div className="font-display text-6xl mt-6 text-brand">Free</div>
              ) : (
                <div className="font-display text-6xl mt-6">${p.price.toLocaleString()}</div>
              )}
              {p.priceSuffix && <div className="text-secondary text-xs tracking-widest uppercase mt-2">{p.priceSuffix}</div>}
              {p.monthly && <div className="text-brand text-sm tracking-widest uppercase mt-1">then ${p.monthly}/{p.monthlyLabel || "mo"}</div>}
              {p.valueSummary && (
                <div
                  className="mt-6 border-y border-brand/30 py-3 text-[11px] md:text-xs text-primary leading-relaxed tracking-wide"
                  data-testid={`pricing-value-${p.id}`}
                >
                  {p.valueSummary}
                </div>
              )}
              <ul className="mt-8 space-y-3 text-secondary text-sm">
                {p.features.map((f)=>(<li key={f} className="flex gap-3"><span className="text-brand">—</span>{f}</li>))}
              </ul>
              {p.whatYouReceive && (
                <details id={`receive-${p.id}`} className="mt-6 group border-t border-white/5 pt-6" data-testid={`pricing-receive-${p.id}`}>
                  <summary className="list-none cursor-pointer flex items-center justify-between gap-3 text-brand text-xs uppercase tracking-[0.22em] hover:opacity-80 transition">
                    <span>What You Receive</span>
                    <span className="transition-transform duration-300 group-open:rotate-45 text-base leading-none" aria-hidden="true">+</span>
                  </summary>
                  <ul className="mt-5 space-y-3 text-secondary text-sm">
                    {p.whatYouReceive.map((item) => (
                      <li key={item} className="flex gap-3"><span className="text-brand">—</span>{item}</li>
                    ))}
                  </ul>
                  {p.whoWeHelp && (
                    <div className="mt-6 pt-6 border-t border-white/5" data-testid={`pricing-who-${p.id}`}>
                      <div className="text-brand text-xs uppercase tracking-[0.22em]">Who We Help</div>
                      <ul className="mt-4 space-y-3 text-secondary text-sm">
                        {p.whoWeHelp.industries.map((ind) => (
                          <li key={ind} className="flex gap-3"><span className="text-brand">—</span>{ind}</li>
                        ))}
                      </ul>
                      <p className="mt-4 text-secondary text-sm italic leading-relaxed">
                        {p.whoWeHelp.closingStatement}
                      </p>
                    </div>
                  )}
                  {p.caseStudies && <CaseStudyTabs items={p.caseStudies} pkgId={p.id} focusId={industry} focusKey={key} />}
                </details>
              )}
              {p.note && (
                <div className="mt-6 border-l-2 border-brand pl-4 text-xs text-secondary leading-relaxed" data-testid={`pricing-note-${p.id}`}>
                  {p.note}
                </div>
              )}
              {p.footnote && (
                <div className="mt-6 pt-6 border-t border-white/5 text-[11px] text-secondary leading-relaxed opacity-80" data-testid={`pricing-footnote-${p.id}`}>
                  {p.footnote}
                </div>
              )}
              <button onClick={bookStrategyCall} data-calendly className={`mt-10 w-full ${p.featured ? "btn-primary" : "btn-secondary"}`} data-testid={`pricing-buy-${p.id}`}>
                {p.isFree ? "Book Free Session" : (p.ctaLabel || "Book a Strategy Call")}
              </button>
            </div>
            </MasonryItem>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- DEMO CENTER --------
function DemoCenter() {
  return (
    <section id="demo" className="py-24 md:py-40 bg-surface">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="overline">06 · The Demonstration</div>
          <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mt-6">
            See How AI Can <em className="text-brand">Transform</em> Your Business.
          </h2>
          <p className="text-secondary mt-8 leading-relaxed text-lg">
            From the promise to the proof — two demonstrations that show exactly how your business runs once Freedom is installed.
          </p>
        </div>

        <div className="mt-24 md:mt-32 space-y-28 md:space-y-40">
          <VideoShowcase
            layout="media-left"
            kind="mp4"
            src="https://customer-assets.emergentagent.com/job_creative-freedom-ai-1/artifacts/669xtjkq_Freedom%20By%20AI%20Design%20-%20Commercial_1080p.mp4"
            poster="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&q=80"
            eyebrow="01 · The Vision"
            title="Stop Missing Customers. Start Capturing Every Opportunity."
            description="Discover how an AI Receptionist answers every call, captures every lead, books appointments, and works 24/7 so your business never misses another opportunity."
          />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start" data-testid="personal-invitation">
            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <PhoneMockup/>
            </div>
            <div className="lg:col-span-7">
              <div className="overline">02 · The Personal Invitation</div>
              <h3 className="font-display font-light text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] mt-6">
                See Your Business <em className="text-brand">Come to Life</em> with AI.
              </h3>

              <p className="text-secondary text-lg leading-relaxed mt-8">Every business has different customers, workflows, and opportunities.</p>
              <p className="text-secondary text-lg leading-relaxed mt-5">Instead of showing a generic prerecorded demonstration, I'd rather show you exactly how an AI Receptionist could work for <em className="text-primary not-italic">your</em> business.</p>
              <p className="text-secondary text-lg leading-relaxed mt-5">During your complimentary AI Strategy Call, I'll demonstrate a customized AI Receptionist experience based on your industry, answer your questions, and identify automation opportunities that can help your business save time, capture more leads, and increase revenue.</p>

              <div className="mt-12">
                <div className="overline mb-6">You'll discover</div>
                <ul className="border-t border-brand">
                  {[
                    "How your AI Receptionist would answer customer calls",
                    "How appointments can be booked automatically",
                    "How leads are qualified before they ever reach you",
                    "How every conversation is synchronized into your CRM",
                    "Where AI can immediately improve efficiency and profitability in your business",
                  ].map((item, i) => (
                    <li key={item} className="flex gap-6 py-5 border-b border-brand items-start" data-testid={`invitation-bullet-${i}`}>
                      <span className="font-mono text-xs text-brand tracking-widest pt-1.5">{String(i+1).padStart(2,'0')}</span>
                      <span className="text-primary leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-secondary text-lg leading-relaxed mt-10 italic">
                Whether we work together or not, you'll leave with valuable insights and practical AI strategies tailored to your business.
              </p>

              <div className="mt-12">
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  data-testid="invitation-strategy-call-cta"
                >
                  Book My Complimentary AI Strategy Call
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-28 md:mt-40 max-w-3xl mx-auto text-center" data-testid="demo-cta-block">
          <h3 className="font-display font-light text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.08]">
            Ready to See What AI Could Do for <em className="text-brand">Your Business?</em>
          </h3>
          <p className="text-secondary text-lg mt-8 leading-relaxed">
            Schedule a complimentary AI Strategy Call and discover how intelligent automation can help you capture more leads, improve customer experience, and create more freedom for your business.
          </p>
          <div className="mt-12 flex justify-center">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              data-testid="demo-strategy-call-cta"
            >
              Book My Complimentary AI Strategy Call
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// -------- CASE STUDIES --------
function CaseStudies() {
  return (
    <section id="cases" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="overline">07 · Client Stories</div>
        <h2 className="font-display font-light text-4xl md:text-5xl mt-4">Real outcomes from real businesses.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          {CASE_STUDIES.map((c, i) => (
            <div key={c.client || i} className="editorial-card p-10" data-testid={`case-${i}`}>
              <div className="overline">Case · {String(i+1).padStart(2,'0')}</div>
              <div className="font-display text-2xl mt-3">{c.client}</div>
              <div className="mt-6"><div className="overline">Problem</div><p className="text-secondary text-sm mt-1">{c.problem}</p></div>
              <div className="mt-4"><div className="overline">Solution</div><p className="text-secondary text-sm mt-1">{c.solution}</p></div>
              <div className="mt-6"><div className="overline">Results</div>
                <ul className="mt-2 space-y-1">{c.results.map((r,j)=><li key={r || j} className="text-primary text-sm">— {r}</li>)}</ul>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t,i)=>(
            <div key={t.name || i} className="p-8 border-l border-brand-hi">
              <div className="font-display text-3xl text-brand leading-none">"</div>
              <p className="text-secondary leading-relaxed mt-4 italic">{t.quote}</p>
              <div className="mt-6"><div className="text-primary text-sm">{t.name}</div><div className="text-muted text-xs uppercase tracking-widest mt-1">{t.role}</div></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- ABOUT --------
function About() {
  return (
    <section id="about" className="py-24 md:py-32 bg-surface">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-16 items-center">
        <div className="md:col-span-5 relative">
          <div className="relative grain overflow-hidden">
            <img src={FOUNDER_IMG} alt="Sophia Jess, Founder & CEO of Freedom By AI Design" className="w-full object-cover"/>
          </div>
        </div>
        <div className="md:col-span-7">
          <div className="overline">08 · About the Founder</div>
          <h2 className="font-display font-light text-4xl md:text-6xl tracking-tighter leading-[1.05] mt-6">Hi, I'm <em className="text-brand">Sophia Jess</em>.</h2>
          <div className="mt-4">
            <div className="text-brand text-sm tracking-[0.18em] uppercase">Founder &amp; CEO · Freedom By AI Design</div>
            <p className="text-primary text-lg mt-3 leading-relaxed">AI Automation Strategist helping businesses work smarter, serve customers faster, and grow through intelligent automation.</p>
          </div>

          <p className="text-secondary text-lg mt-10 leading-relaxed">I founded Freedom By AI Design with a simple belief:</p>
          <blockquote className="border-l border-brand-hi pl-6 mt-5 font-display text-2xl md:text-3xl leading-tight text-primary italic">
            Businesses shouldn't lose revenue because someone couldn't answer the phone.
          </blockquote>

          <p className="text-secondary text-lg mt-8 leading-relaxed">We help companies automate customer communication, appointment scheduling, lead qualification, and the repetitive processes that quietly drain a business — using intelligent AI systems that work 24/7.</p>
          <p className="text-secondary text-lg mt-4 leading-relaxed">My mission is to help entrepreneurs reclaim their time, increase profitability, and build businesses that create more freedom through automation.</p>
          <p className="text-secondary text-lg mt-4 leading-relaxed">Freedom By AI Design combines premium customer experiences with practical AI solutions that allow businesses to scale — without sacrificing service.</p>

          <div className="mt-10 flex gap-4 flex-wrap">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="about-cta-book">Book a Call with Sophia</a>
            <a href="#audit" className="btn-ghost">Or take the audit →</a>
          </div>
        </div>
      </div>
    </section>
  );
}

// -------- FAQ --------
function FAQ() {
  return (
    <section id="faq" className="py-24 md:py-32">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="overline">09 · Questions</div>
        <h2 className="font-display font-light text-4xl md:text-5xl mt-4">Frequently asked.</h2>
        <div className="mt-12 border-t border-white/5">
          {FAQS.map((f,i)=>(
            <details key={f.q || i} className="faq border-b border-white/5 py-6 group" data-testid={`faq-${i}`}>
              <summary className="flex justify-between items-center cursor-pointer">
                <span className="font-display text-xl md:text-2xl pr-6">{f.q}</span>
                <span className="faq-icon text-2xl text-brand">+</span>
              </summary>
              <p className="text-secondary mt-4 leading-relaxed whitespace-pre-line">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- CONTACT --------
function Contact() {
  const attribution = useAttribution();
  const { elapsedMs } = useFormTimer();
  const [hp, setHp] = React.useState("");
  const [form, setForm] = React.useState({
    first_name: "", last_name: "", email: "", phone: "",
    company_name: "", website: "", industry: "",
    service_requested: "General Inquiry", message: "",
  });
  const [done, setDone] = React.useState(false);
  const [err, setErr] = React.useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/leads", {
        ...form,
        name: [form.first_name, form.last_name].filter(Boolean).join(" "),
        business_name: form.company_name,
        business_type: form.industry,
        source: "contact",
        attribution,
        submitted_at: nowISO(),
        time_to_complete_ms: elapsedMs(),
        company_website_alt: hp,
      });
      setDone(true);
    } catch { setErr("Could not send. Please try again."); }
  };
  return (
    <section id="contact" className="py-24 md:py-32 bg-surface">
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="overline">10 · Get Started</div>
          <h2 className="font-display font-light text-4xl md:text-5xl mt-4 leading-tight">Ready to give yourself back your weekends?</h2>
          <p className="text-secondary mt-6 leading-relaxed">Book a complimentary consultation. We'll map your highest-impact automation in under thirty minutes.</p>
          <div className="mt-12 space-y-4">
            <div><div className="overline">Email</div><a href="mailto:freedombyaidesign@gmail.com" className="text-primary mt-1 block">freedombyaidesign@gmail.com</a></div>
            <div><div className="overline">Phone</div><a href="tel:7204194164" className="text-primary mt-1 block" data-testid="contact-phone-link">(720) 419-4164</a></div>
            <div><div className="overline">Studio</div><div className="text-primary mt-1">Denver, Colorado</div></div>
          </div>
        </div>
        <div className="md:col-span-7">
          {done ? (
            <div className="editorial-card p-12 tracing-beam" data-testid="contact-success">
              <div className="font-display text-4xl text-brand">Thank you.</div>
              <p className="text-secondary mt-4 leading-relaxed">Your message landed. Sophia will personally reach out within one business day.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Honeypot value={hp} onChange={setHp}/>
              <input required placeholder="First Name *" className="input-line" value={form.first_name} onChange={(e)=>setForm({...form,first_name:e.target.value})} data-testid="contact-first-name"/>
              <input placeholder="Last Name" className="input-line" value={form.last_name} onChange={(e)=>setForm({...form,last_name:e.target.value})} data-testid="contact-last-name"/>
              <input required type="email" placeholder="Email *" className="input-line" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="contact-email"/>
              <input placeholder="Phone" className="input-line" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="contact-phone"/>
              <input placeholder="Company Name" className="input-line" value={form.company_name} onChange={(e)=>setForm({...form,company_name:e.target.value})} data-testid="contact-company"/>
              <input placeholder="Website" className="input-line" value={form.website} onChange={(e)=>setForm({...form,website:e.target.value})} data-testid="contact-website"/>
              <select className="input-line" value={form.industry} onChange={(e)=>setForm({...form,industry:e.target.value})} data-testid="contact-industry">
                <option value="">Industry</option>
                {["Real Estate","Insurance","Law","Medical","Dental","Med Spa","HVAC","Plumbing","Roofing","Contractors","Property Mgmt","Restaurants","Other"].map(o=><option key={o}>{o}</option>)}
              </select>
              <select className="input-line" value={form.service_requested} onChange={(e)=>setForm({...form,service_requested:e.target.value})} data-testid="contact-service">
                {SERVICE_OPTIONS.map(o=><option key={o}>{o}</option>)}
              </select>
              <textarea rows="5" placeholder="Tell us about your goals…" className="input-line md:col-span-2" value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} data-testid="contact-message"/>
              <button type="submit" className="btn-primary md:col-span-2 w-fit" data-testid="contact-submit">Send Message →</button>
              {err && <p className="text-[#9E7A7A] text-sm md:col-span-2">{err}</p>}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <main>
      <Hero/>
      <TrustBar/>
      <Solutions/>
      <HomeEnhancementsTop/>
      <Industries/>
      <ROICalculator/>
      <BusinessAudit/>
      <Pricing/>
      <DemoCenter/>
      <CaseStudies/>
      <About/>
      <HomeEnhancementsClose/>
      <FAQ/>
      <Contact/>
    </main>
  );
}
